import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import Echo from "laravel-echo";
import moment from "moment";
import slugify from "slugify";
import io from "socket.io-client";
import { v4 as uuid } from "uuid";
import {
  selectIsConnectedToRoom,
  selectLocalPeerID,
  selectPeers,
  useHMSStore,
} from "@100mslive/react-sdk";
import { ToastManager } from "../components/Toast/ToastManager";
import i18n, { LANG_ARR_PRESENTATION } from "../lang";
import {
  setCheckApprove,
  setCheckinMember,
  setImgIdx,
  setRoomName,
  setShowingId,
  setSlideLanguage,
  setType,
} from "../reducers/counter";
import { useSetAppDataByKey } from "../core/components/AppData/useUISettings";
import { useGetUserData } from "../hooks/useGetUserData";
import { getLanguage } from "../core/common/utils";
import { parseClinicPlaceholder } from "@common/utils";
import { useAPIGo } from "../services/api";
import request from "../services/request";
import { APP_DATA } from "../core/common/constants";

window.io = io;

const LiveHodoReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case "SET_CABIN_DATA_SUCCESS":
      return {
        ...state,
        cabins: payload,
      };
    case "IS_JOIN_CABIN":
      return {
        ...state,
        isJoinCabin: payload,
      };
    case "IS_CONFERENCE":
      return {
        ...state,
        isConference: payload,
      };
    case "IS_LIVESTREAM":
      return {
        ...state,
        isLivestream: payload,
      };
    case "IS_MDT":
      return {
        ...state,
        isMdt: payload,
      };
    case "IS_MEETING":
      return {
        ...state,
        isMeeting: payload,
      };
    case "IS_TELEMEDICINE":
      return {
        ...state,
        isTelemedicine: payload,
      };
    case "SELECTED_CABIN_SUCCESS":
      return {
        ...state,
        selectedCabinData: payload,
      };
    case "SELECTED_ROLE_SUCCESS":
      return {
        ...state,
        selectedRole: payload,
      };
    case "SET_LIVE_ROOM_DETAIL_SUCCESS":
      return {
        ...state,
        liveRoomDetail: payload,
      };
    case "SET_CONFERENCE_ROOM_DETAIL_SUCCESS":
      return {
        ...state,
        conferenceRoomDetail: payload,
      };
    case "SET_CIRCLE_ROOM_DETAIL_SUCCESS":
      return {
        ...state,
        circleRoomDetail: payload,
      };
    case "SET_MDT_ROOM_DETAIL_SUCCESS":
      return {
        ...state,
        mdtRoomDetail: payload,
      };
    case "RESET_CONTEXT_STATE_SUCCESS":
      return {
        ...state,
        ...payload,
      };
    case "SELECTED_SCREEN_SHARE_SUCCESS":
      return {
        ...state,
        selectedScreenShare: payload,
      };
    case "GET_CONFERENCE_SCHEDULES_SUCCESS":
      return {
        ...state,
        conferenceSchedules: payload,
      };
    case "GET_TWL_MEET_ROOM_SUCCESS":
      return {
        ...state,
        twlMeetRoom: payload,
      };
    case "GET_TWL_MDT_ROOM_SUCCESS":
      return {
        ...state,
        twlMdtRoom: payload,
      };
    case "SET_CHANNEL_ROOM_SUCCESS":
      return {
        ...state,
        channelRoom: payload,
      };
    case "SET_USER_PEER_MAP":
      return {
        ...state,
        userIdToPeerId: {
          ...(state.userIdToPeerId || {}),
          [payload.user_id]: payload.peer_id,
        },
        peerIdToUser: {
          ...(state.peerIdToUser || {}),
          [payload.peer_id]: {
            user_id: payload.user_id,
            name: payload.name,
            role: payload.role,
          },
        },
      };
    case "REMOVE_USER_PEER_MAP": {
      const userIdToPeerId = { ...(state.userIdToPeerId || {}) };
      const peerIdToUser = { ...(state.peerIdToUser || {}) };
      if (payload?.user_id) {
        const pid = userIdToPeerId[payload.user_id];
        delete userIdToPeerId[payload.user_id];
        if (pid) delete peerIdToUser[pid];
      } else if (payload?.peer_id) {
        const uid = peerIdToUser[payload.peer_id]?.user_id;
        delete peerIdToUser[payload.peer_id];
        if (uid) delete userIdToPeerId[uid];
      }

      return {
        ...state,
        userIdToPeerId,
        peerIdToUser,
      };
    }
    case "SET_CURRENT_HOST":
      return {
        ...state,
        currentHost: payload,
      };
    default:
      return { ...state };
  }
};

export const LiveHodoContext = createContext();

export const LiveHodoContextProvider = ({ children }) => {
  const initialState = {
    cabins: [],
    isJoinCabin: true,
    isConference: false,
    isLivestream: false,
    isMdt: false,
    isMeeting: false,
    isTelemedicine: false,
    selectedCabinData: null,
    selectedRole: "",
    liveRoomDetail: null,
    conferenceRoomDetail: null,
    circleRoomDetail: null,
    mdtRoomDetail: null,
    isAudioShareDisabled: false,
    isScreenShareDisabled: false,
    selectedScreenShare: null,
    conferenceSchedules: null,
    twlMeetRoom: null,
    twlMdtRoom: null,
    channelRoom: null,
    currentHost: null,
  };
  const [stateCtx, dispatch] = useReducer(LiveHodoReducer, initialState);

  const { t } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [presentationTrackId, setPresentationTrackId] = useSetAppDataByKey(
    APP_DATA.presentationTrackId
  );
  const [, setMeetingReportTrackId] = useSetAppDataByKey(
    APP_DATA.meetingReportTrackId
  );

  const languages = LANG_ARR_PRESENTATION;
  const [isPresenting, setIsPresenting] = useState(false);
  const [isMeetingReport, setIsMeetingReport] = useState(false);
  const [isSavingMeetingMinutes, setIsSavingMeetingMinutes] = useState(false);
  const [tabActiveIndex, setTabActiveIndex] = useState(1);
  const [isPMDT, setIsPMDT] = useState(() => {
    const type = (window.localStorage.getItem("type") || "").toLowerCase();
    return type === "pmdt";
  });

  const langCode = window.localStorage.getItem("lang");
  const lang = getLanguage(langCode, languages);
  const [selectedPresentationLang, setSelectedPresentationLang] =
    useState(lang);
  const [hasDoctorAI, setHasDoctorAI] = useState(false);

  const userData = useGetUserData();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const peers = useHMSStore(selectPeers);
  const localPeerId = useHMSStore(selectLocalPeerID);
  const [imgIndexThumb, setImgIndexThumb] = useState(null);
  const [checkinMembers, setCheckinMembersState] = useState([]);

  const setCheckinMembers = arr => {
    if (!Array.isArray(arr)) {
      setCheckinMembersState([]);
      return;
    }
    const normalized = arr.map(item => {
      const userFromTop = item?.user;
      const userFromDoctorUser = item?.doctor?.user;
      const doctor = item?.doctor;
      const fallbackUser = userFromTop || userFromDoctorUser || doctor || item;
      return {
        ...item,
        user: {
          id: fallbackUser?.id,
          name: fallbackUser?.name,
          username: fallbackUser?.username,
          ...(item.user && typeof item.user === "object" ? item.user : {}),
        },
      };
    });
    setCheckinMembersState(normalized);
    dispatchs(setCheckinMember(normalized));
  };

  const createSocketConnection = useCallback(async () => {
    const token = localStorage.getItem("token") || "";
    if (!token || !userData?.id) return;
    if (!window.Echo && typeof io !== "undefined") {
      window.Echo = new Echo({
        broadcaster: "socket.io",
        host: useAPIGo
          ? process.env.REACT_APP_ECHO_URL_DEV
          : process.env.REACT_APP_ECHO_URL_PROD,
        transports: ["websocket"],
        auth: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      });

      window.Echo.connector.socket.on("connect", function () {
        console.log("connected", window.Echo.socketId());
      });
      window.Echo.connector.socket.on("disconnect", function () {
        console.log("disconnected");
      });
      window.Echo.connector.socket.on("reconnecting", function (attemptNumber) {
        console.log("reconnecting", attemptNumber);
      });
    }
  }, [userData]);

  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id || !localPeerId) return;
    const payload = {
      id: userData.id,
      name: userData.name,
      role: userData.role || "doctor",
      peer_id: localPeerId,
    };
    setUserPeerMap({
      user_id: userData.id,
      peer_id: localPeerId,
      name: userData.name,
      role: userData.role,
    });

    stateCtx.channelRoom.whisper("joinRoom", payload);
  }, [
    stateCtx?.channelRoom,
    userData?.id,
    userData?.name,
    userData?.role,
    localPeerId,
  ]);

  useEffect(() => {
    createSocketConnection();
  }, [createSocketConnection]);

  const setCabins = value => {
    dispatch({
      type: "SET_CABIN_DATA_SUCCESS",
      payload: value,
    });
  };

  const setUserPeerMap = ({ user_id, peer_id, name, role }) => {
    dispatch({
      type: "SET_USER_PEER_MAP",
      payload: { user_id, peer_id, name, role },
    });
  };

  const removeUserPeerMap = ({ user_id, peer_id }) => {
    dispatch({
      type: "REMOVE_USER_PEER_MAP",
      payload: { user_id, peer_id },
    });
  };

  const setIsJoinCabin = value => {
    dispatch({
      type: "IS_JOIN_CABIN",
      payload: value,
    });
  };

  const setIsConference = value => {
    dispatch({
      type: "IS_CONFERENCE",
      payload: value,
    });
  };

  const setIsLivestream = value => {
    dispatch({
      type: "IS_LIVESTREAM",
      payload: value,
    });
  };

  const setIsMdt = value => {
    dispatch({
      type: "IS_MDT",
      payload: value,
    });
  };

  const setIsMeeting = value => {
    dispatch({
      type: "IS_MEETING",
      payload: value,
    });
  };

  const setIsTelemedicine = value => {
    dispatch({
      type: "IS_TELEMEDICINE",
      payload: value,
    });
  };

  const setSelectedCabinData = data => {
    dispatch({
      type: "SELECTED_CABIN_SUCCESS",
      payload: data,
    });
  };
  const setSelectedRole = data => {
    dispatch({
      type: "SELECTED_ROLE_SUCCESS",
      payload: data,
    });
  };

  const setLiveRoomDetail = data => {
    dispatch({
      type: "SET_LIVE_ROOM_DETAIL_SUCCESS",
      payload: data,
    });
  };

  const setConferenceRoomDetail = data => {
    dispatch({
      type: "SET_CONFERENCE_ROOM_DETAIL_SUCCESS",
      payload: data,
    });
  };

  const setCircleRoomDetail = data => {
    dispatch({
      type: "SET_CIRCLE_ROOM_DETAIL_SUCCESS",
      payload: data,
    });
  };

  const setMdtRoomDetail = data => {
    dispatch({
      type: "SET_MDT_ROOM_DETAIL_SUCCESS",
      payload: data,
    });
  };

  const setIsAudioShareDisabled = () => {
    stateCtx.isAudioShareDisabled = !stateCtx.isAudioShareDisabled;
  };

  const setIsScreenShareDisabled = () => {
    stateCtx.isScreenShareDisabled = !stateCtx.isScreenShareDisabled;
  };

  const resetContextState = () => {
    dispatch({
      type: "RESET_CONTEXT_STATE_SUCCESS",
      payload: initialState,
    });
  };

  const setSelectedScreenShare = data => {
    dispatch({
      type: "SELECTED_SCREEN_SHARE_SUCCESS",
      payload: data,
    });
  };

  const setCurrentHost = host => {
    dispatch({
      type: "SET_CURRENT_HOST",
      payload: host,
    });
  };

  const setInitiatedPresent = value => {
    initiatedPresentRef.current = !!value;
  };

  const getAges = value => {
    if (!value) return;

    if (value instanceof Date) {
      let bd = moment(value, "YYYY-MM-DD");
      let age = moment().diff(bd, "years");
      return age;
    } else {
      return value;
    }
  };

  // return standardized gender keys for i18n lookup: 'male', 'female', 'unknown'
  // If langCode is provided, return localized label using i18n
  const getGender = (value, langCode) => {
    const key = value === 1 ? "male" : value === 2 ? "female" : "unknown";

    if (!langCode) return key;

    try {
      // use i18n to translate the gender key for the requested language
      const lg = String(langCode).slice(0, 2);
      return i18n.t(`gender.${key}`, { lng: lg });
    } catch (e) {
      // fallback labels
      if (key === "male") return "Male";
      if (key === "female") return "Female";
      return "Unknown";
    }
  };

  const formatInputDateTime = dateTime => {
    if (!dateTime) return moment().format("YYYY-MM-DD HH:mm:ss");
    return moment(dateTime).format("YYYY-MM-DD HH:mm:ss");
  };

  const getMdtTime = value => {
    if (!value) return formatInputDateTime();

    if (typeof value === "string" || value instanceof Date) {
      return formatInputDateTime(value);
    }

    if (value?.concluding?.mdt_time)
      return formatInputDateTime(value.concluding.mdt_time);
    if (value?.operation?.mdt_time)
      return formatInputDateTime(value.operation.mdt_time);
    if (value?.session?.start_time)
      return formatInputDateTime(value.session.start_time);
    if (value?.mdt_room?.start_time)
      return formatInputDateTime(value.mdt_room.start_time);
    if (value?.mdt_room_disease?.start_time)
      return formatInputDateTime(value.mdt_room_disease.start_time);
    if (value?.mdt_date) return formatInputDateTime(value.mdt_date);

    return formatInputDateTime();
  };
  const getLangCode = id => {
    return getNation(id)?.code;
  };

  const getNation = id => {
    if (!id || isNaN(id)) return;
    const found = languages.find(l => l.value === parseInt(id));
    return found;
  };

  const getNationByCode = l => {
    if (!l) return;
    const found = languages.find(item => item.code === l);
    return found;
  };

  const getLangId = l => {
    return getNationByCode(l);
  };

  const getAgesWithOutLang = (value, lang = null) => {
    if (!value) return;

    if (value instanceof Date) {
      let bd = moment(value, "YYYY-MM-DD");
      let age = moment().diff(bd, "years");
      let month = 0;
      if (!age) {
        month = moment().diff(bd, "months");
      }
      // eslint-disable-next-line no-useless-concat
      return age ? age + " " + "tuổi" : month + " " + "tháng tuổi";
    } else {
      return `${value} tuổi`;
    }
  };

  const bucketName = "hodo-resource";
  const aws = window.AWS;
  const prefix = "resources/imagePrint/";
  const region = "ap-southeast-1";
  const IdentityPoolId = "ap-southeast-1:ff53cfbf-2595-4ade-83b8-43e3ad25f279";

  aws.config.region = region; // 1. Enter your region
  aws.config.credentials = new aws.CognitoIdentityCredentials({
    IdentityPoolId: IdentityPoolId, // 2. Enter your identity pool
  });
  aws.config.credentials.get(function (err) {
    if (err) alert(err);
  });

  // Convert base64 (no data: prefix) to Uint8Array
  function base64ToUint8Array(base64) {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  const fileUploadHandler = async base64 => {
    if (!base64 || !base64.length) return;

    // Extract the content type (e.g., image/png or application/pdf) from the data uri
    const contentTypeMatch = base64.match(/^data:([\w\-/+]+);base64,/);
    const contentType = contentTypeMatch ? contentTypeMatch[1] : "image/png";

    // Strip the data uri header generically for any mime type
    const raw = base64.replace(/^data:[\w\-/+]+;base64,/, "");
    const binaryData = base64ToUint8Array(raw);
    // Use the subtype for extension like "png", "jpg", "pdf"
    const type = contentType.split("/")[1];
    const res = await s3ResourcesUploader(
      prefix,
      binaryData,
      type,
      contentType
    );
    return res;
  };
  // Upload a base64 file to the specified S3 key path, then register it with server
  const fileUploadToPath = async (uploadPath, base64Content) => {
    if (!uploadPath) return null;
    try {
      // If caller passed only base64 as first arg, fallback to original handler
      if (!base64Content && uploadPath && uploadPath.startsWith("data:")) {
        return await fileUploadHandler(uploadPath);
      }
      // If uploadPath is an absolute URL (presigned or full URL), extract the S3 key path
      const normalizeToKey = path => {
        if (!path) return path;
        if (/^https?:\/\//i.test(path)) {
          try {
            const u = new URL(path);
            const p = u.pathname.startsWith("/")
              ? u.pathname.slice(1)
              : u.pathname;
            return decodeURIComponent(p);
          } catch {
            return path;
          }
        }
        return path;
      };

      const keyPath = normalizeToKey(uploadPath);
      // initialize pending uploads map
      if (!fileUploadToPath._pendingUploads)
        fileUploadToPath._pendingUploads = {};
      // if there's an in-flight upload for this keyPath, reuse it
      if (fileUploadToPath._pendingUploads[keyPath]) {
        try {
          return await fileUploadToPath._pendingUploads[keyPath];
        } catch (e) {
          // fall through to attempt upload again
        }
      }

      // Convert base64 to binary
      const raw = (base64Content || "").replace(/^data:[\w\-/+]+;base64,/, "");
      const binaryDataArr = base64ToUint8Array(raw);
      // Upload the file directly to S3 at the given path using AWS SDK
      const uploadPromise = (async () => {
        try {
          // helper to upload a raw key (no timestamp suffix)
          const uploadRawToS3 = async (
            keyPath,
            binaryArr,
            contentType = "application/pdf"
          ) => {
            try {
              const bucket = new aws.S3({ params: { Bucket: bucketName } });
              // Ensure we upload a Blob when possible (matches Vue implementation)
              let body = binaryArr;
              // If caller passed a Uint8Array / ArrayBuffer, wrap into a Blob
              if (
                (typeof Uint8Array !== "undefined" &&
                  binaryArr instanceof Uint8Array) ||
                (typeof ArrayBuffer !== "undefined" &&
                  binaryArr instanceof ArrayBuffer)
              ) {
                body = new Blob([binaryArr], { type: contentType });
              }
              const params = {
                Bucket: bucketName,
                Key: keyPath,
                Body: body,
                ACL: "public-read",
                // Do not set ContentEncoding here - upload the raw binary/Blob so
                // the object is stored like the Vue uploader (no base64 encoding header)
                ContentType: contentType,
              };
              const { Location } = await bucket.upload(params).promise();
              return Location;
            } catch (err) {
              console.warn("uploadRawToS3 failed", err);
              return null;
            }
          };

          await uploadRawToS3(keyPath, binaryDataArr, "application/pdf");

          // After uploading to S3, return the S3 key path (do NOT call presign here).
          // The server or viewer should resolve the presigned URL when needed.
          return keyPath;
        } catch (err) {
          console.warn("fileUploadToPath: direct S3 upload failed", err);
          return null;
        }
      })();

      fileUploadToPath._pendingUploads[keyPath] = uploadPromise;
      try {
        return await uploadPromise;
      } finally {
        delete fileUploadToPath._pendingUploads[keyPath];
      }
    } catch (err) {
      console.error("fileUploadToPath unexpected error", err);
      return null;
    }
  };
  const s3ResourcesUploader = async (
    path,
    base64Data,
    type,
    contentType = `image/${type}`
  ) => {
    if (!base64Data || !base64Data.length) return;

    try {
      let bucket = null;

      bucket = new aws.S3({
        params: {
          Bucket: bucketName,
        },
      });

      const params = {
        Bucket: bucketName,
        Key: `${path}${Date.now()}.${type}`, // type is not required
        Body: base64Data,
        ACL: "public-read",
        ContentEncoding: "base64", // required
        // Use the provided contentType so PDF uploads use application/pdf
        ContentType: contentType, // required.
      };
      const { Location } = await bucket.upload(params).promise();
      return Location;
    } catch (error) {
      console.log(error);
    }
  };

  const convertSlug = text => {
    if (!text) return;
    return slugify(text, {
      replacement: "-", // replace spaces with replacement character, defaults to `-`
      remove: /[*+~.()'"!:@]/g, // remove characters that match regex, defaults to `undefined`
      lower: true, // convert to lower case, defaults to `false`
      strict: true, // strip special characters except replacement, defaults to `false`
      locale: "vi", // language code of the locale to use
    });
  };

  const templateHTMLPrint = ({ bodyHTML, title = "", lang = "vi" }) => {
    const template = `<html lang="${lang}"><head><meta charset="UTF-8"><meta http-equiv="X-UA-Compatible" content="IE=edge"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${title}</title><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Tinos:ital,wght@0,400;0,700;1,400;1,700&display=swap" rel="stylesheet"><style>
    /* vietnamese */
    @font-face {
      font-family: "Times";
      font-weight: 700;
      src: url("https://dev.hodo.app/assets/font/tnr/bold.ttf");
    }
    @font-face {
      font-family: "Times";
      font-weight: 700;
      font-style: italic;
      src: url("https://dev.hodo.app/assets/font/tnr/bold-italic.ttf");
    }
    @font-face {
      font-family: "Times";
      font-weight: 400;
      src: url("https://dev.hodo.app/assets/font/tnr/regular.ttf");
    }
    @font-face {
      font-family: "Times";
      font-weight: 400;
      font-style: italic;
      src: url("https://dev.hodo.app/assets/font/tnr/regular-italic.ttf");
    }
    html, body {
      font-family: "Times New Roman", "Times", "Tinos", serif !important;
      font-size: 1.0625rem;
      line-height: 1.5rem;
      font-weight: 500;
      font-style: normal;
      letter-spacing: 0em;
      color: #000000 !important;
    }
     #wrapper {overflow-x: hidden;}@font-face {font-family: "Times";font-weight: 700;src: url("https://dev.hodo.app/assets/font/tnr/bold.ttf");}@font-face {font-family: "Times";font-weight: 700;font-style: italic;src: url("https://dev.hodo.app/assets/font/tnr/bold-italic.ttf");}@font-face {font-family: "Times";font-weight: 400;src: url("https://dev.hodo.app/assets/font/tnr/regular.ttf");}@font-face {font-family: "Times";font-weight: 400;font-style: italic;src: url("https://dev.hodo.app/assets/font/tnr/regular-italic.ttf");}@import url("https://fonts.googleapis.com/css2?family=Tinos:ital,wght@0,400;0,700;1,400;1,700&display=swap");#ElementToPrint, #ElementToPrint *:not([class*=" el-icon-"], [class^="el-icon-"]), #ElementToPrint p, #ElementToPrint span {font-family: "Times New Roman", "Times", "Tinos", serif !important;color: black;}#ElementToPrint {}#CareDetail, #CareDetail i, #CareDetail ul, #CareDetail li, #CareDetail p, #CareDetail strong, #phieuPhauThuatViewDetail, #phieuPhauThuatViewDetail p, #phieuPhauThuatViewDetail strong, #phieuPhauThuatViewDetail i, #PhieuGayMeViewDetail, #PhieuGayMeViewDetail p, #PhieuGayMeViewDetail strong, #PhieuGayMeViewDetail i, #PhieuChamSocViewDetail, #PhieuChamSocViewDetail p, #PhieuChamSocViewDetail strong, #PhieuChamSocViewDetail span {color: black;margin: 0;font-family: "Times New Roman", "Times", "Tinos", serif !important;}#CareDetail, #CareDetail *:not([class*=" el-icon-"], [class^="el-icon-"]), #phieuPhauThuatViewDetail, #phieuPhauThuatViewDetail *:not([class*=" el-icon-"], [class^="el-icon-"]), #PhieuGayMeViewDetail, #PhieuGayMeViewDetail *:not([class*=" el-icon-"], [class^="el-icon-"]), #PhieuChamSocViewDetail, #PhieuChamSocViewDetail *:not([class*=" el-icon-"], [class^="el-icon-"]), #TempPhieuChucNangSong, #TempPhieuChucNangSong *:not([class*=" el-icon-"], [class^="el-icon-"]) {color: black;font-family: "Times New Roman", "Times", "Tinos", serif !important;}#TemplatePrescription, #TemplatePrescription *:not([class*=" el-icon-"], [class^="el-icon-"]), #TemplatePrescription p, #TemplatePrescription div, #TemplatePrescription span, #MedicalRecordsViewDetail, #MedicalRecordsViewDetail *:not([class*=" el-icon-"], [class^="el-icon-"]), #MedicalRecordsViewDetail p, #MedicalRecordsViewDetail label, #MedicalRecordsViewDetail div, #MedicalRecordsViewDetail span, #MedicalRecordsViewDetail strong, #MedicalRecordsViewDetail i, #tmpPhieuGayMe, #tmpPhieuGayMe *:not([class*=" el-icon-"], [class^="el-icon-"]), #tmpPhieuGayMe p, #tmpPhieuGayMe div, #tmpPhieuGayMe span, #tmpPhieuGayMe p, #tmpPhieuGayMe label, #tmpPhieuGayMe div, #tmpPhieuGayMe span, #tmpPhieuGayMe strong, #tmpPhieuGayMe i, #modalPhieuGayMe, #modalPhieuGayMe *:not([class*=" el-icon-"], [class^="el-icon-"]), #modalPhieuGayMe p, #modalPhieuGayMe div, #modalPhieuGayMe span, #modalPhieuGayMe p, #modalPhieuGayMe label, #modalPhieuGayMe div, #modalPhieuGayMe span, #modalPhieuGayMe strong, #modalPhieuGayMe i, #TreatmentViewDetail *:not([class*=" el-icon-"], [class^="el-icon-"]), #TreatmentViewDetail div, #TreatSumsViewDetail div, #TreatmentViewDetail span, #TreatSumsViewDetail span, #CareDetail *:not([class*=" el-icon-"], [class^="el-icon-"]), #CareDetail div, #CareDetail span, #bienbantemp, #bienbantemp *:not([class*=" el-icon-"], [class^="el-icon-"]), #bienbantemp div, #bienbantemp span, #bienbantemp strong, #bienbantemp i, #meetConcludingTemp, #meetConcludingTemp *:not([class*=" el-icon-"], [class^="el-icon-"]), #meetConcludingTemp p, #meetConcludingTemp div, #meetConcludingTemp span, #meetConcludingTemp strong, #meetConcludingTemp i, #TTTQMTemp, #TTTQMTemp *:not([class*=" el-icon-"], [class^="el-icon-"]), #TTTQMTemp p, #TTTQMTemp div, #TTTQMTemp span, #TTTQMTemp strong, #TTTQMTemp i, #IndicationsPrinter, #IndicationsPrinter *:not([class*=" el-icon-"], [class^="el-icon-"]), #IndicationsPrinter div, #IndicationsPrinter span, #IndicationsPrinter i, #IndicationResultPrintTemplate, #IndicationResultPrintTemplate *:not([class*=" el-icon-"], [class^="el-icon-"]), #IndicationResultPrintTemplate p, #IndicationResultPrintTemplate div, #IndicationResultPrintTemplate b, #IndicationResultPrintTemplate i, #IndicationResultPrintTemplate h1, #IndicationResultPrintTemplate h2, #IndicationResultPrintTemplate h3, #IndicationResultPrintTemplate h4, #IndicationResultPrintTemplate h5, #IndicationResultPrintTemplate h6, #IndicationResultPrintTemplate span, #IndicationResultPrintTemplate strong, #TemplateViewGMTM, #TemplateViewGMTM *:not([class*=" el-icon-"], [class^="el-icon-"]), #TemplateViewGMTM p, #TemplateViewGMTM div, #TemplateViewGMTM b, #TemplateViewGMTM i, #TemplateViewGMTM h1, #TemplateViewGMTM h2, #TemplateViewGMTM h3, #TemplateViewGMTM h4, #TemplateViewGMTM h5, #TemplateViewGMTM h6, #TemplateViewGMTM span, #TemplateViewGMTM strong, #PhieuGayMeViewDetail, #PhieuGayMeViewDetail *:not([class*=" el-icon-"], [class^="el-icon-"]), #TempPhieuChucNangSong, #TempPhieuChucNangSong *:not([class*=" el-icon-"], [class^="el-icon-"]) {font-family: "Times New Roman", "Times", "Tinos", serif !important;}#bienbantemp .content-box, #meetConcludingTemp .content-box, #bienbantemp .content-box p, #meetConcludingTemp .content-box p {font-family: "Times New Roman", "Times", "Tinos", serif !important;font-size: 12px;margin-bottom: 0px;}.content-box, .content-box p, .content-box b, .content-box div, .content-box span, .content-box i, .content-box strong {font-family: "Times New Roman", "Times", "Tinos", serif !important;font-size: 12px;margin-bottom: 0px;}.content-box-10i, .content-box-10i p, .content-box-10i b, .content-box-10i div, .content-box-10i span, .content-box-10i i, .content-box-10i strong {font-family: "Times New Roman", "Times", "Tinos", serif !important;font-size: 10px;margin-bottom: 0px;font-style: italic;}.ck-editor__editable {min-height: 200px;}@media (min-width: 768px) {#page-content-wrapper {min-width: 0;}}.v-toast {z-index: 2011 !important;}.v-toast__item {padding: 5px;justify-content: flex-start;box-shadow: 0 3px 10px rgb(0 0 0 / 0.2);}.v-toast__item--default .v-toast__icon {display: block;width: 64px !important;min-width: 64px !important;height: 64px !important;margin-left: 1em;background: url(../../hodologo_256x256.png) no-repeat !important;background-size: 64px 64px !important;}.v-toast__item--default {background-color: #fff !important;}.v-toast__item--default .v-toast__text {font-family: "Roboto" !important;color: black !important;}.mw-135px {min-width: 135px;}.btn-circle {width: 40px;height: 40px;text-align: center;padding: 0;font-size: 20px;line-height: 40px;border-radius: 20px !important;}.notification-tab {min-width: 200px;}.notification-link {color: #232e3c !important;}.notification-link:hover {text-decoration: none;}.notification-link:hover .row {background: #e3e3e3 !important;border-radius: 5px;}.notification-side {top: 0;bottom: 0;position: fixed;overflow-y: scroll;overflow-x: hidden;width: 300px;right: 0;background-color: #f4f6fa;z-index: 1032;}.notification-side::-webkit-scrollbar {width: 5px;height: 10px;}.notification-side::-webkit-scrollbar-thumb {border-radius: 8px;background-color: #c2c9d2;}.bg-drop {z-index: 1031;width: 100vw;height: 100vh;position: fixed;opacity: 0.5;}.btn {font-weight: 400 !important;}#conversation .ck-editor__editable_inline:focus {color: black;}#conversation .ck-editor__editable_inline {max-height: 120px;line-height: 23px;overflow: auto;min-height: 50px;border-left: none !important;border-right: none !important;}#conversation .ck-toolbar_grouping, #conversation .ck.ck-toolbar__items {border: none !important;background: white;}#ElectronicMedicalRecords .ck-editor__editable_inline {line-height: 24px;overflow: auto;min-height: 64px;}#ElectronicMedicalRecords .ck-toolbar_grouping > .ck-toolbar__items {height: 30px;}#ElectronicMedicalRecords .ck-toolbar_grouping > .ck-toolbar__items > button {min-height: 20px !important;min-width: 20px !important;padding: 0;margin: 0 8px;}#ElectronicMedicalRecords .ck-toolbar__items > .ck-toolbar-dropdown {margin: 0 8px;}#ElectronicMedicalRecords .ck-toolbar__items .ck-dropdown__arrow {height: 12px;width: 12px;}#ElectronicMedicalRecords .ck-toolbar__items .ck-dropdown__button .ck-splitbutton__arrow svg {height: 12px;width: 12px;}#ElectronicMedicalRecords .ck-sticky-panel button > svg {height: 16px;width: 16px;}#ElectronicMedicalRecords .ck-dropdown__panel-visible button > svg {height: 44px;width: 44px;}#ElectronicMedicalRecords .ck-dropdown__panel-visible .ck-toolbar_vertical svg {height: 16px;width: 16px;}.toast-push-notify {max-height: 100px;overflow: hidden;}#tagInputSpcl .tags-input-badge {border-radius: 8px;font-weight: 500;font-size: 14px;padding: 9px 25px 9px 10px;background-color: #e7eaf5;margin: 2px 16px 2px 0px;}#tagRemove .tags-input-remove {font-size: 20px;}#tagRemove .tags-input-remove:after {background: #292d32;}#tagRemove .tags-input-remove:before {background: #292d32;}.form--input {border-radius: 4px;border: none;background-color: #f6f8fb;line-height: 32px;font-size: 14px;font-weight: 500;color: #1d2939;width: 100%;padding: 0 8px;}.el-input__inner {border-radius: 4px !important;border: none !important;background-color: #f6f8fb !important;line-height: 32px !important;font-size: 14px !important;font-weight: 500 !important;color: #1d2939 !important;}.el-input__prefix, .el-input__suffix {color: #1d2939 !important;}.el-date-editor.el-input, .el-date-editor.el-input__inner {width: 100% !important;}.form--input::-webkit-input-placeholder {font-size: 14px;font-weight: 400;}.form--input:-ms-input-placeholder {font-size: 14px;font-weight: 400;}.form--input::placeholder {font-size: 14px;font-weight: 400;}input[type="radio"] {width: 1rem;height: 1rem;}.form--input:focus-visible {outline: solid 1px #20419b !important;}.v-html-p-0 p {margin-bottom: 0 !important;}#TemplatePrescription, #MedicalRecordsExport, #TreatmentViewDetail, #TreatSumsViewDetail, #phieuPhauThuatViewDetail, #PhieuGayMeViewDetail, #PhieuChamSocViewDetail, #CareDetail {}#MedicalRecordsViewDetail .h-detail2 {height: calc(100vh - 11rem);}#MedicalRecordsViewDetail input[type="radio"] {display: none;}#MedicalRecordsViewDetail input[type="radio"] + span {display: flex;justify-content: center;align-items: center;font-size: 9px;}#MedicalRecordsViewDetail input[type="radio"] + span::before {content: " ";height: 24px !important;width: 26px;border: 1px solid;display: block;margin-left: 2px;}#MedicalRecordsViewDetail input[type="radio"]:checked + span::before {content: "X";font-size: 14px;height: 24px !important;width: 24px;border: 1px solid;display: flex;align-items: center;justify-content: center;font-style: normal;}#MedicalRecordsViewDetail .border-box {border: 1px solid;}#MedicalRecordsViewDetail .border-bottom-item {border-bottom: 1px solid;}#MedicalRecordsViewDetail .border-right-item {border-right: 1px solid;}#MedicalRecordsViewDetail .box-h-item {min-height: 24px;min-width: 24px;}#MedicalRecordsViewDetail .content-none {width: 80%;border-bottom: 2px dotted;margin-bottom: 6px;}#MedicalRecordsViewDetail .min-h-20 {min-height: 5rem;}th div, td div {word-break: inherit !important;}.el-date-editor .el-range-separator {width: auto !important;}.el-date-editor--daterange {background-color: #fff !important;border-radius: 4px !important;border: 1px solid #dcdfe6 !important;box-sizing: border-box !important;color: #606266 !important;display: inline-block !important;font-size: inherit !important;height: 40px !important;line-height: 40px !important;outline: 0 !important;padding: 0 15px !important;transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1) !important;width: 100% !important;}.el-range-editor .el-range-input {background-color: initial;}.el-table__cell div {word-break: break-word !important;}.el-dialog__headerbtn .el-dialog__close {color: #000 !important;font-size: 22px !important;}
    .hodo-print {
      font-family: "Times New Roman", "Times", "Tinos", serif !important;
      letter-spacing: 0em;
      color: #000000 !important;
    }
    .hodo-print * {
      font-family: "Times New Roman", "Times", "Tinos", serif !important;
      letter-spacing: 0em;
      color: #000000 !important;
    }
    .hodo-print img {
      max-width: 100%;
    }
    .hodo-print img,
    .hodo-print svg {
      vertical-align: middle;
    }
    .hodo-print .content-none {
      width: 100%;
      border-bottom: 2px dotted;
    }
    .hodo-print .whitespace-nowrap {
      white-space: nowrap;
    }
    .hodo-print .font {
      font-family: "Times New Roman", Tinos, Times, serif !important;
    }
    .hodo-print .checkbox {
      display: inline-flex;
      cursor: pointer;
      position: relative;
    }
    .hodo-print p {
      margin: 0;
    }
    .hodo-print .text-italic {
      font-style: italic;
    }
    .hodo-print .relative {
      position: relative;
    }
    .hodo-print .absolute {
      position: absolute;
    }
    .hodo-print .top-0 {
      top: 0;
    }
    .hodo-print .right-0 {
      right: 0;
    }
    .hodo-print .left-0 {
      left: 0;
    }
    .hodo-print .px-8 {
      padding-right: 2rem;
      padding-left: 2rem;
    }
    .hodo-print .overflow-hidden {
      overflow: hidden;
    }
    .hodo-print .float-left {
      float: left;
    }
    .hodo-print .float-right {
      float: right;
    }
    .hodo-print .float-none {
      float: none;
    }
    .hodo-print .block {
      display: block;
    }
    .hodo-print .inline-block {
      display: inline-block !important;
    }
    .hodo-print .flex {
      display: flex;
    }
    .hodo-print .flex-col {
      flex-direction: column;
    }
    .hodo-print .align-top {
      vertical-align: top;
    }
    .hodo-print .text-center {
      text-align: center;
    }
    .hodo-print .text-left {
      text-align: left;
    }
    .hodo-print .text-right {
      text-align: right;
    }
    .hodo-print .normal-case {
      text-transform: none;
    }
    .hodo-print .uppercase {
      text-transform: uppercase;
    }
    .hodo-print .capitalize {
      text-transform: capitalize;
    }
    .hodo-print .leading-none {
      line-height: 1;
    }
    .hodo-print .align-middle {
      vertical-align: middle;
    }
    .hodo-print .justify-center {
      justify-content: center;
    }
    .hodo-print .justify-end {
      justify-content: end;
    }
    .hodo-print .justify-between {
      justify-content: space-between;
    }
    .hodo-print .items-start {
      align-items: flex-start;
    }
    .hodo-print .items-center {
      align-items: center;
    }
    .hodo-print .col-2 {
      width: 16.6666667% !important;
      display: inline-block !important;
      vertical-align: top;
    }
    .hodo-print .col-3 {
      width: 25% !important;
      display: inline-block !important;
      vertical-align: top;
    }
    .hodo-print .col-4 {
      width: 33.3333333% !important;
      display: inline-block !important;
      vertical-align: top;
    }
    .hodo-print .col-5 {
      width: 41% !important;
      display: inline-block !important;
      vertical-align: top;
    }
    .hodo-print .col-6 {
      width: 50% !important;
      display: inline-block !important;
      vertical-align: top;
    }
    .hodo-print .col-7 {
      width: 58.3333333% !important;
      display: inline-block !important;
      vertical-align: top;
    }
    .hodo-print .col-12 {
      width: 100% !important;
      display: inline-block !important;
      vertical-align: top;
    }
    .hodo-print .w-full {
      width: 100% !important;
    }
    .hodo-print .w-half {
      width: 50% !important;
    }
    .hodo-print .w-one-third {
      width: 33.333% !important;
    }
    .hodo-print .w-two-thirds {
      width: 66.667% !important;
    }
    .hodo-print .font-normal {
      font-weight: 400;
    }
    .hodo-print .font-bold {
      font-weight: 700;
    }
    .hodo-print .text-xs,
    .hodo-print .text-xs *,
    .hodo-print .fs-12,
    .hodo-print .fs-12 *,
    .hodo-print .content-size.fs-12,
    .hodo-print .content-size.fs-12 * {
      font-size: 0.75rem;
      line-height: 1rem;
    }
    .hodo-print .fs-13,
    .hodo-print .fs-13 *,
    .hodo-print .content-size.fs-13,
    .hodo-print .content-size.fs-13 * {
      font-size: 0.8125rem;
      line-height: 1.25rem;
    }
    .hodo-print .fs-14,
    .hodo-print .fs-14 *,
    .hodo-print .content-size.fs-14,
    .hodo-print .content-size.fs-14 * {
      font-size: 0.875rem;
      line-height: 1.25rem;
    }
    .hodo-print .text-sm,
    .hodo-print .text-sm * .hodo-print .fs-16,
    .hodo-print .fs-16 *,
    .hodo-print .content-size.fs-16,
    .hodo-print .content-size.fs-16 * {
      font-size: 1rem;
      line-height: 1.25rem;
    }
    .hodo-print .text-base,
    .hodo-print .text-base * {
      font-size: 1rem;
      line-height: 1.5rem;
    }
    .hodo-print .fs-17,
    .hodo-print .fs-17 *,
    .hodo-print .content-size.fs-17,
    .hodo-print .content-size.fs-17 * {
      font-size: 1.0625rem;
      line-height: 1.5rem;
    }
    .hodo-print .text-lg,
    .hodo-print .text-lg *,
    .hodo-print .fs-18,
    .hodo-print .fs-18 *,
    .hodo-print .content-size.fs-18,
    .hodo-print .content-size.fs-18 * {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    .hodo-print .text-xl,
    .hodo-print .text-xl *,
    .hodo-print .fs-20,
    .hodo-print .fs-20 *,
    .hodo-print .content-size.fs-20,
    .hodo-print .content-size.fs-20 * {
      font-size: 1.25rem;
      line-height: 1.75rem;
    }
    .hodo-print .fs-22,
    .hodo-print .fs-22 *,
    .hodo-print .content-size.fs-22,
    .hodo-print .content-size.fs-22 * {
      font-size: 1.375rem;
      line-height: 1.825rem;
    }
    .hodo-print .text-2xl,
    .hodo-print .text-2xl *,
    .hodo-print .fs-24,
    .hodo-print .fs-24 *,
    .hodo-print .content-size.fs-24,
    .hodo-print .content-size.fs-24 * {
      font-size: 1.5rem;
      line-height: 2rem;
    }
    .hodo-print .my-3 {
      margin-top: 0.75rem;
      margin-bottom: 0.75rem;
    }
    .hodo-print .px-1 {
      padding-left: 0.25rem;
      padding-right: 0.25rem;
    }
    .hodo-print .py-1 {
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;
    }
    .hodo-print .pt-12 {
      padding-top: 3rem;
    }
    .hodo-print .p-0 {
      padding: 0;
    }
    .hodo-print .p-1 {
      padding: 0.25rem;
    }
    .hodo-print .pt-1 {
      padding-top: 0.25rem;
    }
    .hodo-print .pb-1 {
      padding-bottom: 0.25rem;
    }
    .hodo-print .pb-2 {
      padding-bottom: 0.5rem;
    }
    .hodo-print .pl-2 {
      padding-left: 0.5rem;
    }
    .hodo-print .pt-3 {
      padding-top: 0.75rem;
    }
    .hodo-print .pl-3 {
      padding-left: 0.75rem;
    }
    .hodo-print .pl-12 {
      padding-left: 3rem;
    }
    .hodo-print .pl-16 {
      padding-left: 4rem;
    }
    .hodo-print .pr-12 {
      padding-right: 3rem;
    }
    .hodo-print .pr-16 {
      padding-right: 4rem;
    }
    .hodo-print .mt-1 {
      margin-top: 0.25rem;
    }
    .hodo-print .mt-2 {
      margin-top: 0.5rem;
    }
    .hodo-print .mt-3 {
      margin-top: 0.75rem;
    }
    .hodo-print .mt-4 {
      margin-top: 1rem;
    }
    .hodo-print .m-6 {
      margin: 1.5rem;
    }
    .hodo-print .ml-6 {
      margin-left: 1.5rem;
    }
    .hodo-print .mt-6 {
      margin-top: 1.5rem;
    }
    .hodo-print .mt-8 {
      margin-top: 2rem;
    }
    .hodo-print .mt-10 {
      margin-top: 2.5rem;
    }
    .hodo-print .mt-12 {
      margin-top: 3rem;
    }
    .hodo-print .mt-16 {
      margin-top: 4rem;
    }
    .hodo-print .mb-16 {
      margin-bottom: 4rem;
    }
    .hodo-print .mt-20 {
      margin-top: 5rem;
    }
    .hodo-print .mt-24 {
      margin-top: 6rem;
    }
    .hodo-print .mt-44 {
      margin-top: 11rem;
    }
    .hodo-print .ml-auto {
      margin-left: auto;
    }
    .hodo-print .mx-1 {
      margin-left: 0.25rem;
      margin-right: 0.25rem;
    }
    .hodo-print .ml-1 {
      margin-left: 0.25rem;
    }
    .hodo-print .ml-2 {
      margin-left: 0.5rem;
    }
    .hodo-print .ml-3 {
      margin-left: 0.75rem;
    }
    .hodo-print .ml-4 {
      margin-left: 1rem;
    }
    .hodo-print .ml-5 {
      margin-left: 1.25rem;
    }
    .hodo-print .mr-1 {
      margin-right: 0.25rem;
    }
    .hodo-print .mb-0 {
      margin-bottom: 0;
    }
    .hodo-print .mb-1 {
      margin-bottom: 0.25rem;
    }
    .hodo-print .mb-2 {
      margin-bottom: 0.5rem;
    }
    .hodo-print .mb-3 {
      margin-bottom: 0.75rem;
    }
    .hodo-print .mb-6 {
      margin-bottom: 1.5rem;
    }
    .hodo-print .mb-8 {
      margin-bottom: 2rem;
    }
    .hodo-print .hodo-table figure {
      margin: 0 0 1rem !important;
    }
    .hodo-print .hodo-table table {
      border-collapse: collapse !important;
      width: 100% !important;
      border: 1px solid #dbdde0 !important;
      margin-top: 1rem !important;
    }
    .hodo-print .hodo-table.border-black table {
      border-color: black !important;
    }
    .hodo-print .hodo-table table thead th {
      text-transform: uppercase !important;
    }
    .hodo-print .hodo-table table td,
    .hodo-print .hodo-table table th {
      padding: 8px !important;
      border: 1px solid #dbdde0 !important;
    }
    .hodo-print .hodo-table.border-black table td,
    .hodo-print .hodo-table.border-black table th {
      border-color: black !important;
    }
    .box-number {
      border: 1px solid black;
      height: 1.5rem !important;
    }
    .box-item {
      font-family: "Times New Roman", Tinos, Times, serif !important;
      height: 1.5rem !important;
      width: 1.5rem !important;
      border-right: 1px solid;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .box-item-bhyt {
      font-family: "Times New Roman", Tinos, Times, serif !important;
      height: 0.5rem !important;
      padding: 0.5rem;
      border-right: 1px solid;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .box-item-bhyt:last-child,
    .box-item:last-child {
      border-right: none;
    }
    .checkbox {
      display: inline-flex;
      cursor: pointer;
      position: relative;
    }
    .hodo-print .table-width-full table {
      width: 100% !important;
    }
    .hodo-print .el-table--border,
    .hodo-print .el-table--group {
      border-top: 1px solid #000;
      border-right: 1px solid #000;
      border-left: 1px solid #000;
    }
    .hodo-print .el-table th.el-table__cell.is-leaf {
      border-right: 1px solid #000;
      border-bottom: 1px solid #000;
    }
    .hodo-print .el-table__row:last-child,
    .hodo-print .el-table__cell:last-child,
    .hodo-print .el-table th.el-table__cell.is-leaf:last-child {
      border-right: none;
    }
    .hodo-print .el-table__cell {
      border-bottom: 1px solid #000;
      border-right: 1px solid #000;
    }
    .hodo-print .el-table--border th.el-table__cell > div.cell {
      line-height: 1.25 !important;
    }
    .hodo-print thead th.padding-header {
      padding: 0 8px !important;
    }
    .hodo-print thead th.header-text-left {
      text-align: left !important;
      padding: 0 8px !important;
    }
    .hodo-print .break-words {
      overflow-wrap: break-word !important;
      word-break: break-word !important;
      word-wrap: break-word !important;
      white-space: break-spaces !important;
    }
    .hodo-print .border-box {
      border: 1px solid #000;
    }
    .html2pdf__page-break {
      page-break-before: always;
    }
    .hodo-print .h-3 {
      height: 0.75rem;
    }
    .hodo-print .w-3 {
      width: 0.75rem;
    }
    .hodo-print .h-40 {
      height: 10rem;
    }
    .hodo-print .border {
      border: 1px solid #e0e0e0 !important;
    }
    .hodo-print .border-t {
      border-top: 1px solid #000;
    }
    .hodo-print .border-b {
      border-bottom: 1px solid #000;
    }
    .hodo-print .border-l {
      border-left: 1px solid #000;
    }
    .hodo-print .border-r {
      border-right: 1px solid #000;
    }
    .hodo-print .italic {
      font-style: italic;
    }
    </style></head><body>${bodyHTML}</body></html>`;
    return template;
  };

  const base64 = {
    decode: s => window.btoa(unescape(encodeURIComponent(s))),
    encode: b => decodeURIComponent(escape(window.atob(b))),
  };

  useEffect(() => {
    const hasIsConference = localStorage.getItem("type") === "conference";
    setIsConference(hasIsConference);
  }, []);

  useEffect(() => {
    const hasIsLivestream = localStorage.getItem("type") === "streaming";
    setIsLivestream(hasIsLivestream);
  }, []);

  useEffect(() => {
    const hasIsMdt = localStorage.getItem("type") === "mdt";
    setIsMdt(hasIsMdt);
  }, []);

  useEffect(() => {
    const hasIdMeeting = localStorage.getItem("type") === "meeting";
    setIsMeeting(hasIdMeeting);
  }, []);

  useEffect(() => {
    const hasIsTelemedicine = localStorage.getItem("type") === "telemedicine";
    setIsTelemedicine(hasIsTelemedicine);
  }, []);

  const getToken = async (userId, role, roomId) => {
    try {
      const defaultToken = localStorage.getItem("token");
      if (!defaultToken) return null;

      const params = {
        role,
        room_id: roomId,
        user_id: userId?.toString(),
      };
      const res = await request.generateToken(params);
      const token = res?.data?.token;

      if (!token) {
        throw Error(res.data?.message);
      }

      return token;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const getConferenceSchedules = async paramsData => {
    try {
      const params = {
        ...paramsData,
      };
      const res = await request.getListConferenceSchedules(params);

      if (!res?.data) return;

      dispatch({
        type: "GET_CONFERENCE_SCHEDULES_SUCCESS",
        payload: res.data,
      });
      return res.data;
    } catch (error) {
      console.log(error);
    }
  };

  const getMeetRoomTwl = useCallback(
    async meet_room_id => {
      const roomId =
        meet_room_id ||
        window.localStorage.getItem("meet_room_id") ||
        stateCtx.circleRoomDetail?.meet_room_id;
      if (!roomId) return;

      try {
        const res = await request.getTwlMeetRoom(roomId);
        if (!res?.data) return;

        dispatch({
          type: "GET_TWL_MEET_ROOM_SUCCESS",
          payload: res.data,
        });
        return res.data;
      } catch (error) {
        console.log(error);
      }
    },
    [stateCtx.circleRoomDetail?.meet_room_id]
  );

  const dispatchs = useDispatch();

  useEffect(() => {
    async function setMeetRoomName() {
      const meetRoom = await getMeetRoomTwl();
      if (meetRoom) dispatchs(setRoomName(meetRoom));
    }
    setMeetRoomName();
  }, [getMeetRoomTwl, dispatchs]);

  const getMdtRoomTwl = useCallback(async () => {
    if (!stateCtx.mdtRoomDetail?.room) return;

    try {
      dispatch({
        type: "GET_TWL_MDT_ROOM_SUCCESS",
        payload: stateCtx.mdtRoomDetail?.room,
      });
      return stateCtx.mdtRoomDetail?.room;
    } catch (error) {
      console.log(error);
    }
  }, [stateCtx.mdtRoomDetail?.room]);

  useEffect(() => {
    getMdtRoomTwl();
  }, [getMdtRoomTwl]);

  useEffect(() => {
    console.log("isPresenting :", isPresenting);
    isPresenting ? setPresentationTrackId(uuid()) : setPresentationTrackId();
  }, [isPresenting, setPresentationTrackId]);

  useEffect(() => {
    console.log("isMeetingReport :", isMeetingReport);
    isMeetingReport
      ? setMeetingReportTrackId(uuid())
      : setMeetingReportTrackId();
  }, [isMeetingReport, setMeetingReportTrackId]);

  const agendaAll = useSelector(state => state.counter.agendaAll);
  const slideLanguages = useSelector(state => state.counter.slideLanguages);
  const showingId = useSelector(state => state.counter.showingId);

  const handleConnectChannelRoom = useCallback(() => {
    if (
      (!stateCtx.twlMdtRoom?.room_name && !stateCtx.twlMeetRoom?.room_name) ||
      !window.Echo ||
      typeof window.Echo === "undefined"
    )
      return;

    const twlRoomName =
      stateCtx.twlMdtRoom?.room_name || stateCtx.twlMeetRoom?.room_name;

    if (!twlRoomName) return;

    const channelRoom = window.Echo?.private(`twl_room.${twlRoomName}`);

    dispatch({
      type: "SET_CHANNEL_ROOM_SUCCESS",
      payload: channelRoom,
    });
  }, [stateCtx.twlMdtRoom?.room_name, stateCtx.twlMeetRoom?.room_name]);

  useEffect(() => {
    handleConnectChannelRoom();
  }, [handleConnectChannelRoom]);

  // Track previous peers to detect when someone leaves (including refresh)
  const prevPeersRef = useRef([]);
  const initiatedPresentRef = useRef(false);
  useEffect(() => {
    const prev = prevPeersRef.current || [];
    const currentIds = new Set((peers || []).map(p => p.id));

    const left = prev.filter(p => !currentIds.has(p.id));
    if (left && left.length) {
      left.forEach(l => {
        const mappedUser = stateCtx.peerIdToUser?.[l.id];
        const fakeUserId = mappedUser?.user_id || null;
        removeUserPeerMap({ peer_id: l.id });
        if (fakeUserId && stateCtx.currentHost?.id === fakeUserId) {
          const available = (peers || []).filter(p => p.id !== l.id);
          let newHost = null;
          if (available && available.length) {
            const candidate = available.find(
              p => !!stateCtx.peerIdToUser?.[p.id]
            );
            if (candidate) {
              const mapped = stateCtx.peerIdToUser?.[candidate.id];
              if (mapped?.user_id) {
                newHost = {
                  id: mapped.user_id,
                  name: mapped.name,
                  role: mapped.role,
                };
              }
            }
          }
          if (newHost) {
            setCurrentHost(newHost);
            stateCtx.channelRoom?.whisper("changeSlideShow", {
              id: newHost.id,
              name: newHost.name,
              role: newHost.role,
              show_slide: isPresenting,
              showing_id: showingId,
            });
          } else {
            setCurrentHost(null);
          }
        }
      });
    }
    prevPeersRef.current = peers || [];
  }, [
    peers,
    stateCtx.peerIdToUser,
    stateCtx.userIdToPeerId,
    stateCtx.channelRoom,
    stateCtx.currentHost,
    isPresenting,
    showingId,
  ]);

  // Attempt to notify the channel when the user refreshes or closes the page.
  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id) return;

    const sendQuit = event => {
      const payload = {
        id: userData?.id || null,
        name: userData?.name || null,
        role: userData?.role || "doctor",
        peer_id: stateCtx.userIdToPeerId?.[userData?.id] || null,
      };
      stateCtx.channelRoom.whisper("quitRoom", payload);
    };

    window.addEventListener("beforeunload", sendQuit);
    window.addEventListener("pagehide", sendQuit);

    return () => {
      window.removeEventListener("beforeunload", sendQuit);
      window.removeEventListener("pagehide", sendQuit);
    };
  }, [
    stateCtx?.channelRoom,
    userData?.id,
    stateCtx.userIdToPeerId,
    userData?.name,
    userData?.role,
  ]);

  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id) return;

    const userOnlineListener = data => {
      if (data?.id === userData?.id) return;
      // console.log("userOnline", data);
    };

    const quitListener = data => {
      if (data?.id === userData?.id) return;
      console.log("quitRoom", data);
      removeUserPeerMap({ user_id: data?.id, peer_id: data?.peer_id });
      if (stateCtx.currentHost?.id === data?.id) {
        let newHost = null;
        const available = (peers || []).filter(p => p.id !== data?.peer_id);
        if (available && available.length) {
          const candidate = available.find(
            p => !!stateCtx.peerIdToUser?.[p.id]
          );
          if (candidate) {
            const mapped = stateCtx.peerIdToUser?.[candidate.id];
            if (mapped?.user_id) {
              newHost = {
                id: mapped.user_id,
                name: mapped.name,
                role: mapped.role,
              };
            }
          }
        }

        if (newHost) {
          setCurrentHost(newHost);
          stateCtx.channelRoom.whisper("changeSlideShow", {
            id: newHost.id,
            name: newHost.name,
            role: newHost.role,
            show_slide: isPresenting,
            showing_id: showingId,
          });
        } else {
          setCurrentHost(null);
        }
      }
    };

    const changeModePresentListener = data => {
      if (!data?.id || data?.id === userData?.id) return;
      setCurrentHost(data);
      setIsPresenting(data?.show_slide);
    };

    stateCtx.channelRoom.listenForWhisper("userOnline", userOnlineListener);
    stateCtx.channelRoom.listenForWhisper("quitRoom", quitListener);
    stateCtx.channelRoom.listenForWhisper(
      "changeModePresent",
      changeModePresentListener
    );

    return () => {
      stateCtx.channelRoom.stopListeningForWhisper(
        "userOnline",
        userOnlineListener
      );
      stateCtx.channelRoom.stopListeningForWhisper("quitRoom", quitListener);
      stateCtx.channelRoom.stopListeningForWhisper(
        "changeModePresent",
        changeModePresentListener
      );
    };
    // keep listeners in sync with these values
  }, [
    stateCtx?.channelRoom,
    userData?.id,
    peers,
    isPresenting,
    showingId,
    stateCtx?.currentHost?.id,
    stateCtx?.peerIdToUser,
    stateCtx?.userIdToPeerId,
  ]);

  useEffect(() => {
    if (
      isPresenting &&
      !stateCtx.currentHost?.id &&
      initiatedPresentRef.current
    ) {
      setCurrentHost({
        id: userData.id,
        name: userData.name,
        role: userData.role || "doctor",
      });
    }
  }, [isPresenting, stateCtx.currentHost, userData]);

  // Listen for joinRoom whisper to update the current host and slide show state
  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id) return;

    const listener = data => {
      console.log("joinRoom", data);
      if (data?.peer_id) {
        setUserPeerMap({
          user_id: data.id,
          peer_id: data.peer_id,
          name: data.name,
          role: data.role,
        });
      }
      if (data.id !== userData.id && userData.id === stateCtx.currentHost?.id) {
        stateCtx.channelRoom.whisper(data.id, {
          id: stateCtx.currentHost?.id || userData.id,
          name: stateCtx.currentHost?.name || userData.name,
          role: stateCtx.currentHost?.role || "doctor",
          showing_id: showingId,
          show_slide: isPresenting,
          slide_languages: Object.fromEntries(
            Object.entries(slideLanguages).map(([slideId, lang]) => [
              slideId,
              lang.code,
            ])
          ),
        });
        stateCtx.channelRoom.whisper("userOnline", { ...userData });
      }
    };

    stateCtx.channelRoom.listenForWhisper("joinRoom", listener);

    return () => {
      stateCtx.channelRoom.stopListeningForWhisper("joinRoom", listener);
    };
  }, [
    stateCtx?.channelRoom,
    userData,
    isPresenting,
    stateCtx?.currentHost,
    showingId,
    slideLanguages,
  ]);

  // Listen for changeSlideShow whisper to update the current host and slide show state
  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id) return;

    const listener = data => {
      console.log("changeSlideShow", data);
      let showingId = data?.showing_id;
      if (showingId === "start") {
        dispatchs(setShowingId("start"));
        if (agendaAll[0]) dispatchs(setType(agendaAll[0].type));
      } else if (showingId === "end") {
        dispatchs(setShowingId("end"));
        if (agendaAll && agendaAll.length > 0)
          dispatchs(setType(agendaAll[agendaAll.length - 1].type));
      } else if (
        typeof showingId === "string" &&
        showingId.startsWith("__clinic_")
      ) {
        dispatchs(setShowingId(showingId));
        const { clinicId } = parseClinicPlaceholder(showingId);
        const clinicItems = agendaAll.filter(
          a => String(a?.clinic?.id || "") === String(clinicId)
        );
        if (clinicItems && clinicItems.length > 0) {
          dispatchs(setType(clinicItems[0].type));
        }
      } else {
        const currentAgenda = agendaAll.find(
          agenda => agenda.id === data?.showing_id
        );
        if (!currentAgenda) return;
        dispatchs(setShowingId(data?.showing_id));
        dispatchs(setType(currentAgenda.type));
      }
      setCurrentHost({
        id: data.id,
        name: data.name,
        role: data.role,
      });
    };

    stateCtx.channelRoom.listenForWhisper("changeSlideShow", listener);

    return () => {
      stateCtx.channelRoom.stopListeningForWhisper("changeSlideShow", listener);
    };
  }, [stateCtx?.channelRoom, userData, agendaAll, slideLanguages, dispatchs]);

  // Listen for changeSlideShow whisper when joining the room
  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id) return;

    const listener = data => {
      if (data?.id === userData?.id) return;
      console.log("changeSlideShow on joinRoom", data);
      setIsPresenting(data?.show_slide);
      if (data?.slide_languages) {
        Object.entries(data.slide_languages).forEach(([slideId, code]) => {
          const lang =
            LANG_ARR_PRESENTATION.find(l => l.code === code) ||
            LANG_ARR_PRESENTATION[0];
          dispatchs(setSlideLanguage({ slideId, lang }));
        });
      }
      let showingId = data?.showing_id;
      if (showingId === "start") {
        dispatchs(setShowingId("start"));
        if (agendaAll[0]) dispatchs(setType(agendaAll[0].type));
      } else if (showingId === "end") {
        dispatchs(setShowingId("end"));
        if (agendaAll && agendaAll.length > 0)
          dispatchs(setType(agendaAll[agendaAll.length - 1].type));
      } else {
        const currentAgenda = agendaAll.find(
          agenda => agenda.id === data?.showing_id
        );
        if (!currentAgenda) return;
        dispatchs(setShowingId(data?.showing_id));
        dispatchs(setType(currentAgenda.type));
      }
      setCurrentHost({
        id: data.id,
        name: data.name,
        role: data.role,
      });
    };

    stateCtx.channelRoom.listenForWhisper(userData?.id, listener);

    return () => {
      stateCtx.channelRoom.stopListeningForWhisper(userData?.id, listener);
    };
  }, [
    stateCtx?.channelRoom,
    userData?.id,
    agendaAll,
    slideLanguages,
    dispatchs,
  ]);

  // Listen for ThumbImgShow whisper to update the image index
  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id) return;

    const listener = data => {
      if (data?.id === userData?.id) return;
      console.log("ThumbImgShow", data);
      setImgIndexThumb(data?.index);
    };

    stateCtx.channelRoom.listenForWhisper("ThumbImgShow", listener);

    return () => {
      stateCtx.channelRoom.stopListeningForWhisper("ThumbImgShow", listener);
    };
  }, [stateCtx?.channelRoom, userData]);
  useEffect(() => {
    dispatchs(setImgIdx(imgIndexThumb));
  }, [imgIndexThumb, dispatchs]);

  // Listen for Approve whisper to update the approval state
  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id) return;

    const listener = data => {
      dispatchs(setCheckApprove(data.index));
      console.log("Approve", data);
      if (data?.id === userData?.id) return;
    };

    stateCtx.channelRoom.listenForWhisper("Approve", listener);

    return () => {
      stateCtx.channelRoom.stopListeningForWhisper("Approve", listener);
    };
  }, [stateCtx?.channelRoom, userData?.id, dispatchs]);

  // Listen for ChangeTab whisper to update the tab type
  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id) return;

    const listener = data => {
      console.log("ChangeTab", data);
      dispatchs(setType(data.type));
      if (data?.id === userData?.id) return;
    };

    stateCtx.channelRoom.listenForWhisper("ChangeTab", listener);

    return () => {
      stateCtx.channelRoom.stopListeningForWhisper("ChangeTab", listener);
    };
  }, [stateCtx?.channelRoom, userData?.id, dispatchs]);

  const handleListenChangePresentLanguage = useCallback(
    data => {
      setSelectedPresentationLang(data?.lang);

      if (showingId && data?.lang) {
        dispatchs(setSlideLanguage({ slideId: showingId, lang: data.lang }));
      }

      if (isConnected && isPresenting) {
        ToastManager.addToast({
          variant: "success",
          title: `${data?.name} has changed presentation language to ${t(data?.lang?.label)}.`,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isConnected, isPresenting, showingId, dispatchs]
  );

  // Listen for changePresentLanguage whisper to update the selected presentation language
  useEffect(() => {
    if (!stateCtx?.channelRoom || !userData?.id) return;

    const listener = data => {
      if (!data?.lang || data?.id === userData?.id) return;
      handleListenChangePresentLanguage(data);
    };

    stateCtx.channelRoom.listenForWhisper("changePresentLanguage", listener);

    return () => {
      stateCtx.channelRoom.stopListeningForWhisper(
        "changePresentLanguage",
        listener
      );
    };
  }, [stateCtx?.channelRoom, userData, handleListenChangePresentLanguage]);

  // When showingId changes, update the selected presentation language
  useEffect(() => {
    if (!showingId || showingId === "start" || showingId === "end") {
      return;
    }
    setSelectedPresentationLang(slideLanguages[showingId]);
  }, [showingId, slideLanguages, setSelectedPresentationLang]);

  const handleSaveMeetingMinutes = async (paramsData, isNewDay) => {
    let res;

    try {
      const params = {
        ...paramsData,
        title: `Meeting Minutes ${dayjs().format("HH:mm - DD/MM/YYYY")}`,
        meet_time: dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
        status: 1,
      };
      setIsSavingMeetingMinutes(true);

      if (isNewDay) {
        res = await request.createMeetingMinutes(params);
      } else {
        // eslint-disable-next-line no-unused-vars
        const { title, meet_time, concluding_id, ...newParams } = params;
        res = await request.updateMeetingMinutes(concluding_id, newParams);
      }

      ToastManager.addToast({
        variant: "success",
        title: `You saved meeting minutes.`,
      });

      return res?.data;
    } catch (error) {
      console.log(error);
      ToastManager.addToast({
        title: error.message,
        variant: "error",
      });
    } finally {
      setIsSavingMeetingMinutes(false);
    }

    return res;
  };

  const contextData = {
    stateCtx,
    checkinMembers,
    setCheckinMembers,
    isPresenting,
    isMeetingReport,
    selectedPresentationLang,
    hasDoctorAI,
    isSavingMeetingMinutes,
    imgIndexThumb,
    tabActiveIndex,
    setTabActiveIndex,
    setIsPresenting,
    setIsMeetingReport,
    setSelectedPresentationLang,
    setHasDoctorAI,
    isPMDT,
    setIsPMDT,
    setInitiatedPresent,
    setCabins,
    setIsJoinCabin,
    setIsConference,
    setIsLivestream,
    setIsMdt,
    setIsMeeting,
    setIsTelemedicine,
    setSelectedCabinData,
    setSelectedRole,
    setLiveRoomDetail,
    setConferenceRoomDetail,
    setCircleRoomDetail,
    setMdtRoomDetail,
    setIsAudioShareDisabled,
    setIsScreenShareDisabled,
    resetContextState,
    setSelectedScreenShare,
    setCurrentHost,
    setUserPeerMap,
    removeUserPeerMap,
    getToken,
    getConferenceSchedules,
    handleConnectChannelRoom,
    handleSaveMeetingMinutes,
    getMeetRoomTwl,
    getAges,
    getGender,
    getMdtTime,
    formatInputDateTime,
    getNation,
    getNationByCode,
    getLangId,
    getLangCode,
    createSocketConnection,
    templateHTMLPrint,
    base64,
    getAgesWithOutLang,
    fileUploadHandler,
    fileUploadToPath,
    s3ResourcesUploader,
    convertSlug,
  };

  return (
    <LiveHodoContext.Provider value={contextData}>
      {children}
    </LiveHodoContext.Provider>
  );
};
